import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { isRunActive } from "@/hooks/jobs/useAITopMatches";
import type { useAITopMatches } from "@/hooks/jobs/useAITopMatches";
import { AITopMatchesIcon } from "./AITopMatchesIcon";
import { AITopMatchesLauncher } from "./AITopMatchesLauncher";
import { AITopMatchesProgress } from "./AITopMatchesProgress";
import { AITopMatchesResults } from "./AITopMatchesResults";

interface AITopMatchesModalProps {
	isOpen: boolean;
	onClose: () => void;
	/** Run state, owned by the parent so the run is followed while the pop-up is closed */
	topMatches: ReturnType<typeof useAITopMatches>;
	jobTitle?: string;
	companyName?: string;
	existingCandidateIds: string[];
	onCandidatesAdded: () => void;
}

export function AITopMatchesModal({ isOpen, onClose, topMatches, jobTitle, companyName, existingCandidateIds, onCandidatesAdded }: AITopMatchesModalProps) {
	const { t } = useTranslation();
	const { run, loading, starting, adding, candidatesById, isStale, startRun, cancelRun, resumeRun, addCandidates } = topMatches;
	const [showLauncher, setShowLauncher] = useState(false);

	// Closing never cancels the run: reopening reattaches to it
	useEffect(() => {
		if (isOpen)
			setShowLauncher(false);
	}, [isOpen]);

	const active = isRunActive(run);
	const completed = run?.status === "completed";
	const view = loading ? "loading"
		: active ? "progress"
			: completed && !showLauncher ? "results"
				: "launcher";

	const handleStart = async (instructions: string, excludeStatuses: string[]) => {
		await startRun(instructions, excludeStatuses);
		setShowLauncher(false);
	};

	const handleAdd = async (candidateIds: string[]) => {
		const added = await addCandidates(candidateIds);
		if (added > 0) {
			onCandidatesAdded();
			onClose();
		}
		return added;
	};

	const lastError = run?.status === "failed"
		? t("aiTopMatches.toasts.failed", { error: run.error ?? "" })
		: run?.status === "cancelled" ? t("aiTopMatches.toasts.cancelled") : null;

	const subtitle = view === "progress" ? t("aiTopMatches.progress.subtitle")
		: view === "results" ? t("aiTopMatches.results.subtitle", {
			count: run?.results?.items?.length ?? 0,
			time: run?.completed_at ? new Date(run.completed_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "",
		})
			: t("aiTopMatches.subtitle");

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent
				className={cn(
					"flex max-h-[90vh] w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-sm p-0 sm:rounded-sm",
					view === "results" ? "h-[90vh] max-w-6xl" : "max-w-3xl",
				)}
			>
				<div className="flex shrink-0 items-center gap-3 border-b border-border px-5 py-4 pr-12">
					<div className="rounded-sm bg-purple-600 p-2">
						<AITopMatchesIcon className="h-5 w-5 text-white" working={active} done={completed} />
					</div>
					<div className="min-w-0 flex-1">
						<DialogTitle className="truncate text-lg font-semibold text-foreground">
							{t("aiTopMatches.title")}
							{jobTitle && (
								<span className="font-normal text-muted-foreground"> — {jobTitle}{companyName ? ` · ${companyName}` : ""}</span>
							)}
						</DialogTitle>
						<DialogDescription className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</DialogDescription>
					</div>
					{view === "results" && (
						<Button variant="outline" size="sm" className="shrink-0" onClick={() => setShowLauncher(true)}>
							<RotateCcw className="mr-1.5 h-3.5 w-3.5" />
							{t("aiTopMatches.results.runAgain")}
						</Button>
					)}
				</div>

				{view === "loading" && (
					<div className="flex h-48 items-center justify-center">
						<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
					</div>
				)}
				{view === "launcher" && (
					<AITopMatchesLauncher
						initialInstructions={run?.instructions ?? ""}
						initialExcludeStatuses={run?.options?.excludeStatuses ?? ["hired"]}
						starting={starting}
						error={showLauncher ? null : lastError}
						onStart={handleStart}
						onBack={completed ? () => setShowLauncher(false) : undefined}
					/>
				)}
				{view === "progress" && run && (
					<AITopMatchesProgress run={run} isStale={isStale} onCancel={cancelRun} onResume={resumeRun} />
				)}
				{view === "results" && run && (
					<AITopMatchesResults
						run={run}
						candidatesById={candidatesById}
						existingCandidateIds={existingCandidateIds}
						adding={adding}
						onAdd={handleAdd}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
}
